import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XCircle, Rocket } from 'lucide-react';
import { supabase } from '../services/supabase';
import { PageContainer } from '../components/layout/PageContainer';
import { Logo } from '../components/common/Logo';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Button } from '../components/common/Button';
import { trackEvent } from '../utils/analytics';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function LaunchCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'exchanging' | 'redirecting' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleLaunchToken = async () => {
      // Get the token from URL params (either 't' or 'token')
      const token = searchParams.get('t') || searchParams.get('token');

      if (!token) {
        setErrorMessage('Link inválido. Nenhum token encontrado.');
        setStatus('error');
        return;
      }

      console.log('[Launch] Processing launch token...');
      trackEvent('btv_launch_token_attempt', { tokenPrefix: token.substring(0, 8) });

      try {
        // First, sign out any existing session to avoid conflicts
        console.log('[Launch] Clearing any existing session...');
        await supabase.auth.signOut();
        localStorage.removeItem('sb-access-token');

        // Exchange the token for a magic link
        setStatus('exchanging');

        const response = await fetch(`${API_URL}/api/launch/exchange`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          console.error('[Launch] Token exchange failed:', data.error);
          trackEvent('btv_launch_token_failed', {
            tokenPrefix: token.substring(0, 8),
            error: data.error?.code
          });

          // Friendly error messages
          const errorMessages: Record<string, string> = {
            'TOKEN_NOT_FOUND': 'Este link não é válido. Verifique se copiou o link corretamente.',
            'NO_EMAIL': 'Este link não possui um email associado. Entre em contato conosco.',
            'USER_CREATE_ERROR': 'Não foi possível criar sua conta. Tente novamente.',
            'SESSION_ERROR': 'Não foi possível iniciar sua sessão. Tente novamente.',
          };

          setErrorMessage(errorMessages[data.error?.code] || data.error?.message || 'Erro ao processar o link');
          setStatus('error');
          return;
        }

        // Success! Redirect to the magic link
        console.log('[Launch] Token exchanged successfully, redirecting...');
        trackEvent('btv_launch_token_success', {
          tokenPrefix: token.substring(0, 8),
          email: data.data.email
        });

        setStatus('redirecting');

        // Small delay for UX, then redirect to magic link
        setTimeout(() => {
          window.location.href = data.data.magicLink;
        }, 1000);

      } catch (error) {
        console.error('[Launch] Error exchanging token:', error);
        trackEvent('btv_launch_token_error', {
          tokenPrefix: token.substring(0, 8),
          error: error instanceof Error ? error.message : 'unknown'
        });
        setErrorMessage('Erro de conexão. Verifique sua internet e tente novamente.');
        setStatus('error');
      }
    };

    handleLaunchToken();
  }, [searchParams]);

  return (
    <PageContainer className="flex items-center justify-center min-h-screen px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <Logo size="lg" className="mb-8" />

        {(status === 'loading' || status === 'exchanging') && (
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner size="lg" />
            <p className="text-white/60">
              {status === 'loading' ? 'Verificando seu acesso...' : 'Preparando sua conta...'}
            </p>
          </div>
        )}

        {status === 'redirecting' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="w-20 h-20 rounded-full bg-success-green/20 flex items-center justify-center">
              <Rocket className="w-10 h-10 text-success-green" />
            </div>
            <h2 className="text-xl font-semibold text-white">Bem-vindo ao BT Vision!</h2>
            <p className="text-white/60">Redirecionando...</p>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="w-20 h-20 rounded-full bg-coral/20 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-coral" />
            </div>
            <h2 className="text-xl font-semibold text-white">Algo deu errado</h2>
            <p className="text-white/60 max-w-xs">{errorMessage}</p>
            <div className="flex flex-col gap-2 mt-4">
              <Button onClick={() => navigate('/login')} variant="primary">
                Fazer login manualmente
              </Button>
              <Button onClick={() => window.location.reload()} variant="secondary">
                Tentar novamente
              </Button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </PageContainer>
  );
}
